import React, { useState, useEffect } from 'react';
import { ArrowRight, Brain, CheckCircle, Heart, Home, Truck, Phone, PlaneTakeoff, GraduationCap, Activity } from 'lucide-react';
import AnimatedBackground from '../AnimatedBackground';
import ParticleSystem from '../ParticleSystem';
import GradientBackground from '../GradientBackground';
import { BackgroundAnalysisStatus } from '../../types';
import { formatTime } from '../../services/backgroundAnalysisService';
import { getABTestMode, saveSuggestedQuestions } from '../../services/abTestService';
import { validateAndSendExpertStat } from '../../services/expertValidationService';
import { useTranslation } from 'react-i18next';

interface PersonalLandingProps {
  onStartAnalysis: (description?: string, expert?: string, question?: string, noQuestion?: boolean) => void;
  onCancel?: () => void;
  onGoToForm?: () => void;
  onAbortAnalysis?: () => void; // Callback для прерывания анализа
  isLoading?: boolean;
  backgroundAnalysisStatus?: BackgroundAnalysisStatus | null;
  error?: string | null;
  onClearError?: () => void;
  onClearValidationError?: () => void; // Callback для очистки validation error статуса
}

const PersonalLanding: React.FC<PersonalLandingProps> = ({ 
  onStartAnalysis, 
  onGoToForm, 
  onCancel,
  onAbortAnalysis,
  isLoading = false,
  backgroundAnalysisStatus = null,
  error,
  onClearError,
  onClearValidationError
}) => {
  const { i18n } = useTranslation();
  const [description, setDescription] = useState('');
  const [lastSubmittedDescription, setLastSubmittedDescription] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Состояния для модального окна с выбором вопроса
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [customQuestion, setCustomQuestion] = useState('');
  const [questionChoice, setQuestionChoice] = useState<string | null>(null);
  const [processedFailedTaskId, setProcessedFailedTaskId] = useState<string | null>(null);
  
  // Состояния для хранения выбранного вопроса
  const [selectedQuestion, setSelectedQuestion] = useState<string | undefined>(undefined);
  const [noQuestion, setNoQuestion] = useState(false);

  // Автоматическая прокрутка в начало страницы при монтировании компонента
  useEffect(() => {
    // Плавная прокрутка в начало страницы
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // useEffect для мониторинга validation_error с поддержкой A/B тестов
  useEffect(() => {
    if (
      backgroundAnalysisStatus?.status === 'failed' &&
      backgroundAnalysisStatus?.data?.type === 'validation_error' &&
      backgroundAnalysisStatus?.data?.examples &&
      backgroundAnalysisStatus.taskId !== processedFailedTaskId
    ) {
      const abTestMode = getABTestMode();
      
      if (abTestMode === 'A') {
        // Режим A: показываем модальное окно с выбором вопроса
        console.log('🔄 Показываем модальное окно с выбором вопроса на PersonalLanding (режим A)');
        setShowQuestionModal(true);
        setQuestionChoice(null);
        setCustomQuestion('');
        setProcessedFailedTaskId(backgroundAnalysisStatus.taskId);
      } else {
        // Режим B: сохраняем вопросы и запускаем анализ без вопроса
        console.log('🔄 Сохраняем вопросы и запускаем анализ без вопроса на PersonalLanding (режим B)');
        const suggestedQuestions = {
          questions: backgroundAnalysisStatus.data.examples,
          originalRequest: description,
          expert: undefined,
          person: undefined,
          customExpert: undefined,
          customPerson: undefined
        };
        
        saveSuggestedQuestions(suggestedQuestions);
        
        // Валидируем эксперта/личность перед автоматическим перезапуском
        validateAndSendExpertStat(
          undefined,
          undefined,
          undefined,
          undefined,
          i18n.language
        ).then(() => {
          // Запускаем анализ без вопроса после валидации
          onStartAnalysis(description.trim(), undefined, undefined, true);
        }).catch((error) => {
          console.error('❌ PersonalLanding - Ошибка валидации при автоматическом перезапуске:', error);
          // В случае ошибки валидации все равно запускаем анализ
          onStartAnalysis(description.trim(), undefined, undefined, true);
        });
        
        setProcessedFailedTaskId(backgroundAnalysisStatus.taskId);
      }
    }
  }, [backgroundAnalysisStatus, processedFailedTaskId, description, onStartAnalysis]);

  // Проверка валидности описания
  const MAX_DESCRIPTION_LENGTH = 300;
  const MIN_DESCRIPTION_LENGTH = 15;
  const currentLength = description.length;
  const trimmedLength = description.trim().length;
  const isDescriptionValid = trimmedLength >= MIN_DESCRIPTION_LENGTH && currentLength <= MAX_DESCRIPTION_LENGTH;

  const handleGetStarted = () => {
    if (onGoToForm) {
      onGoToForm();
    } else {
      onStartAnalysis();
    }
  };

  const handleQuickAnalysis = async () => {
    if (description.trim()) {
      try {
        // 1. Сначала валидируем эксперта/личность и отправляем статистику
        console.log('🔍 PersonalLanding - Начинаем валидацию эксперта/личности...');
        await validateAndSendExpertStat(
          undefined,
          undefined,
          undefined,
          undefined,
          i18n.language
        );
        
        const finalDescription = description.trim();
        
        // 2. Проверяем, изменился ли запрос
        if (finalDescription !== lastSubmittedDescription) {
          const abTestMode = getABTestMode();
          
          console.log('🔍 PersonalLanding - Отправляем данные:', {
            description: finalDescription,
            expert: undefined,
            abTestMode
          });
          
          setLastSubmittedDescription(finalDescription);
          setIsTyping(false);
          onStartAnalysis(finalDescription, undefined);
        }
      } catch (error) {
        console.error('❌ PersonalLanding - Ошибка при валидации эксперта/личности:', error);
        // В случае ошибки валидации все равно запускаем анализ
        // (статистика уже отправлена в сервисе)
        
        const finalDescription = description.trim();
        
        if (finalDescription !== lastSubmittedDescription) {
          setLastSubmittedDescription(finalDescription);
          setIsTyping(false);
          onStartAnalysis(finalDescription, undefined);
        }
      }
    }
  };

  // Функции для работы с модальным окном с выбором вопроса
  const handleQuestionChoice = (choice: string) => {
    setQuestionChoice(choice);
    if (choice === 'custom') {
      setCustomQuestion('');
    }
  };

  const handleExampleQuestionSelect = (example: string) => {
    setQuestionChoice(example);
    setCustomQuestion('');
  };

  const handleCustomQuestionChange = (value: string) => {
    setCustomQuestion(value);
    if (value.trim()) {
      setQuestionChoice('custom');
    }
  };

  const handleQuestionModalSubmit = async () => {
    let question: string | undefined;
    let noQuestion = false;
    
    if (questionChoice === 'none') {
      noQuestion = true;
      question = undefined;
    } else if (questionChoice === 'custom') {
      question = customQuestion.trim();
      noQuestion = false;
    } else if (questionChoice) {
      question = questionChoice;
      noQuestion = false;
    }
    
    // Сохраняем выбранный вопрос в состоянии
    setSelectedQuestion(question);
    setNoQuestion(noQuestion);
    
    setShowQuestionModal(false);
    setQuestionChoice(null);
    setCustomQuestion('');
    
    try {
      // 1. Сначала валидируем эксперта/личность и отправляем статистику
      console.log('🔍 PersonalLanding - Валидация эксперта/личности в модальном окне...');
      await validateAndSendExpertStat(
        undefined,
        undefined,
        undefined,
        undefined,
        i18n.language
      );
      
      // 2. Запускаем анализ с выбранным вопросом после валидации
      onStartAnalysis(description.trim(), undefined, question, noQuestion);
    } catch (error) {
      console.error('❌ PersonalLanding - Ошибка при валидации эксперта/личности в модальном окне:', error);
      // В случае ошибки валидации все равно запускаем анализ
      // (статистика уже отправлена в сервисе)
      
      onStartAnalysis(description.trim(), undefined, question, noQuestion);
    }
  };

  const handleQuestionModalCancel = () => {
    setShowQuestionModal(false);
    setQuestionChoice(null);
    setCustomQuestion('');
    
    // Очищаем выбранный вопрос
    setSelectedQuestion(undefined);
    setNoQuestion(false);
    
    // Сбрасываем lastSubmittedDescription, чтобы можно было запустить анализ заново
    setLastSubmittedDescription('');
    
    // Очищаем validation error статус в родительском компоненте
    if (onClearValidationError) {
      onClearValidationError();
    }
  };

  const handleClearSelectedQuestion = () => {
    // Прерываем анализ, если он запущен
    if (onAbortAnalysis) {
      onAbortAnalysis();
    }
    
    // Очищаем выбранный вопрос
    setSelectedQuestion(undefined);
    setNoQuestion(false);
    
    // Сбрасываем lastSubmittedDescription, чтобы можно было запустить анализ заново
    setLastSubmittedDescription('');
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newDescription = e.target.value;
    setDescription(newDescription);
    
    // Если анализ активен и пользователь начал печатать, отменяем анализ
    if (backgroundAnalysisStatus && backgroundAnalysisStatus.status !== 'completed' && backgroundAnalysisStatus.status !== 'failed' && !isTyping) {
      if (onCancel) {
        onCancel();
      }
    }
    setIsTyping(true);
  };

  const handleExampleClick = () => {
    const examples = [
      "Покупать ли квартиру в новостройке?",
      "Менять ли работу на позицию в крупной компании?",
      "Переезжать ли в Санкт-Петербург?",
      "Покупать ли Tesla Model 3?",
      "Инвестировать ли в Bitcoin сейчас?",
      "Переходить ли на удаленную работу в IT?",
      "Покупать ли дом в Подмосковье?",
      "Менять ли смартфон на iPhone 15?",
      "Ехать ли в отпуск в Турцию?",
      "Начинать ли заниматься бегом?"
    ];
    
    const randomExample = examples[Math.floor(Math.random() * examples.length)];
    setDescription(randomExample);
  };

  const features = [
    {
      icon: Heart,
      title: 'Личный подход',
      description: 'Анализ учитывает ваши ценности и приоритеты'
    },
    {
      icon: Home,
      title: 'Жизненные решения',
      description: 'От покупок до переездов - все важные выборы'
    },
    {
      icon: Truck,
      title: 'Финансовая выгода',
      description: 'Экономьте деньги на правильных решениях'
    },
    {
      icon: Phone,
      title: 'Удобство',
      description: 'Анализируйте решения в любое время и месте'
    }
  ];

  const benefits = [
    'Принимайте решения без стресса',
    'Экономьте деньги на правильных выборах',
    'Избегайте импульсивных покупок',
    'Улучшайте качество жизни',
    'Планируйте будущее осознанно',
    'Делитесь решениями с семьей'
  ];

  const categories = [
    {
      icon: Home,
      title: 'Недвижимость',
      description: 'Покупка, продажа, аренда, переезд'
    },
    {
      icon: Truck,
      title: 'Транспорт',
      description: 'Автомобили, мотоциклы, велосипеды'
    },
    {
      icon: PlaneTakeoff,
      title: 'Путешествия',
      description: 'Отпуска, командировки, переезды'
    },
    {
      icon: GraduationCap,
      title: 'Образование',
      description: 'Курсы, обучение, сертификаты'
    },
    {
      icon: Activity,
      title: 'Здоровье',
      description: 'Спорт, питание, медицинские решения'
    },
    {
      icon: Phone,
      title: 'Технологии',
      description: 'Гаджеты, подписки, сервисы'
    }
  ];

  const testimonials = [
    {
      name: 'Елена Козлова',
      role: 'Дизайнер',
      text: 'Plinus помог мне принять решение о покупке квартиры. Теперь я уверена, что сделала правильный выбор!'
    },
    {
      name: 'Дмитрий Волков',
      role: 'Инженер',
      text: 'Благодаря анализу ИИ я избежал покупки дорогого, но ненужного автомобиля. Сэкономил 500 тысяч рублей.'
    }
  ];



  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <GradientBackground />
      <AnimatedBackground />
      <ParticleSystem />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-600/10 to-orange-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-pink-100 text-pink-800 text-sm font-medium mb-8">
              <Heart className="w-4 h-4 mr-2" />
              Для личных решений
            </div>
            
            <h1 className="text-5xl md:text-6xl heading-hero gradient-hero mb-6">
              Принимайте жизненные решения с{' '}
              <span className="heading-3d text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-orange-600">
                ИИ
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto text-lead">
              От покупок до переездов - принимайте взвешенные решения для улучшения 
              качества вашей жизни с помощью искусственного интеллекта
            </p>
            
            {/* Форма быстрого анализа */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/20">
                <div className="mb-4">
                  <label htmlFor="personal-description" className="block text-sm font-medium text-gray-700 mb-2">
                    Опишите вашу личную ситуацию
                  </label>
                  <textarea
                    id="personal-description"
                    value={description}
                    onChange={handleDescriptionChange}
                    placeholder="Например: Стоит ли переезжать в другой город? Или: Менять ли работу?"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={3}
                    maxLength={MAX_DESCRIPTION_LENGTH}
                  />
                </div>

                {/* Отображение выбранного вопроса */}
                {(selectedQuestion || noQuestion) && (
                  <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-blue-800">
                          {noQuestion ? 'Общий анализ' : 'Анализ с вопросом'}
                        </h4>
                        <p className="text-sm text-blue-700 mt-1">
                          {noQuestion 
                            ? 'Будет проведен общий анализ ситуации без конкретного вопроса'
                            : `"${selectedQuestion}"`
                          }
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleClearSelectedQuestion}
                        className="flex-shrink-0 ml-3 text-blue-400 hover:text-blue-600 transition-colors"
                        title="Убрать вопрос и прервать анализ"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Отображение ошибки анализа */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-red-800">Ошибка анализа</h3>
                        <p className="text-sm text-red-700 mt-1">
                          {error}
                        </p>
                      </div>
                      {onClearError && (
                        <button
                          onClick={onClearError}
                          className="ml-3 flex-shrink-0 p-2 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-lg transition-colors -mt-1"
                          title="Закрыть"
                        >
                          <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleQuickAnalysis}
                    disabled={isLoading || !isDescriptionValid || (description.trim() === lastSubmittedDescription && lastSubmittedDescription !== '' && !error)}
                    className={`flex-1 inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none relative overflow-hidden ${
                      backgroundAnalysisStatus && backgroundAnalysisStatus.status !== 'completed' && backgroundAnalysisStatus.status !== 'failed'
                        ? 'btn bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 active:bg-primary-800'
                        : ''
                    }`}
                  >
                    {/* Фоновый прогресс-бар */}
                    {backgroundAnalysisStatus && backgroundAnalysisStatus.status !== 'completed' && backgroundAnalysisStatus.status !== 'failed' && (
                      <div className="absolute inset-0 bg-blue-400 transition-all duration-300 z-0" 
                           style={{ 
                             width: backgroundAnalysisStatus.timeSinceStart && backgroundAnalysisStatus.averageAnalysisTime 
                               ? `${Math.min(100, (backgroundAnalysisStatus.timeSinceStart / backgroundAnalysisStatus.averageAnalysisTime) * 100)}%` 
                               : '0%' 
                           }} />
                    )}

                    {/* Содержимое кнопки */}
                    <div className="relative z-10 flex items-center justify-center">
                      {backgroundAnalysisStatus ? (
                        <>
                          <div className="bg-white rounded-full mr-2">
                            <img 
                              src="/images/preloader-plus-minus.svg" 
                              alt="Загрузка" 
                              className="h-4 w-4 sm:h-5 sm:w-5"
                            />
                          </div>
                          <div>
                            <div className="font-medium text-center">
                              {backgroundAnalysisStatus.status === 'pending' ? 'Ожидание в очереди' : 'Анализ выполняется'}
                              {backgroundAnalysisStatus.estimatedTimeRemaining !== null && backgroundAnalysisStatus.estimatedTimeRemaining !== undefined && backgroundAnalysisStatus.estimatedTimeRemaining > 0 && (
                                <span className="text-xs opacity-90 ml-2">
                                  • Осталось: {formatTime(backgroundAnalysisStatus.estimatedTimeRemaining)}
                                </span>
                              )}
                            </div>
                          </div>
                        </>
                      ) : isLoading ? (
                        <>
                          <div className="bg-white rounded-full mr-2">
                            <img 
                              src="/images/preloader-plus-minus.svg" 
                              alt="Загрузка" 
                              className="h-4 w-4 sm:h-5 sm:w-5"
                            />
                          </div>
                          Отправляем в очередь...
                        </>
                      ) : (
                        <>
                          <Brain className="w-5 h-5 mr-2" />
                          {error && description.trim() === lastSubmittedDescription ? 'Повторить анализ' : (description.trim() === lastSubmittedDescription && lastSubmittedDescription !== '' ? 'Запрос не изменился' : 'Анализировать решение')}
                        </>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={handleExampleClick}
                    className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Пример
                  </button>
                </div>
              </div>
            </div>
            
            <a
              href={`/${lang}/analysis`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
            >
              Начать бесплатно
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl heading-hero mb-4">
              Категории решений
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Анализируйте любые жизненные ситуации и принимайте правильные решения
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-pink-500 to-orange-500 text-white rounded-lg mb-4">
                  <category.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg heading-small mb-2">{category.title}</h3>
                <p className="text-gray-600">{category.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-r from-pink-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl heading-section mb-4">
              Почему выбирают Plinus
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Наша платформа помогает принимать решения, которые улучшают вашу жизнь
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-pink-500 to-orange-500 text-white rounded-lg mb-4">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg heading-small mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl heading-hero mb-4">
              Преимущества для вас
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3 p-4">
                <CheckCircle className="w-6 h-6 text-pink-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-r from-pink-50 to-orange-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl heading-section mb-4">
              Отзывы пользователей
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20 hover:shadow-3xl transition-all duration-300 hover:scale-105">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-pink-600 to-orange-600 rounded-2xl flex items-center justify-center text-white font-bold text-lg mr-4">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="text-lg heading-small">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-pink-600 to-orange-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl heading-hero text-white mb-4">
            Готовы улучшить качество жизни?
          </h2>
          <p className="text-xl text-pink-100 mb-8">
            Начните принимать взвешенные решения уже сегодня
          </p>
          <a
            href={`/${lang}/analysis`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-white text-pink-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
          >
            Начать бесплатный анализ
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </section>

      {/* Модальное окно с выбором вопроса */}
      {showQuestionModal && 
       backgroundAnalysisStatus?.status === 'failed' &&
       backgroundAnalysisStatus?.data?.type === 'validation_error' &&
       backgroundAnalysisStatus?.data?.examples && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg heading-small">
                  Выберите вопрос для анализа
                </h3>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                В вашей ситуации не обнаружен конкретный вопрос для анализа. 
                Выберите один из предложенных вариантов или сформулируйте свой вопрос.
              </p>
              
              <div className="space-y-3">
                {/* Примеры вопросов на верхнем уровне */}
                {backgroundAnalysisStatus.data.examples.map((example, index) => (
                  <div key={index} className="p-3">
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="radio"
                        name="questionChoice"
                        value={example}
                        checked={questionChoice === example}
                        onChange={() => handleExampleQuestionSelect(example)}
                        className="text-primary-600 focus:ring-primary-500 mt-1"
                      />
                      <span className="ml-3 text-base font-medium text-gray-700 leading-relaxed">
                        {example}
                      </span>
                    </label>
                  </div>
                ))}
                
                {/* Поле ввода своего вопроса на верхнем уровне */}
                <div className="p-3">
                  <div className="flex items-start">
                    <input
                      type="radio"
                      name="questionChoice"
                      value="custom"
                      checked={questionChoice === 'custom'}
                      onChange={() => handleQuestionChoice('custom')}
                      className="text-primary-600 focus:ring-primary-500 mt-3"
                    />
                    <div className="ml-3 flex-1">
                      <input
                        type="text"
                        value={customQuestion}
                        onChange={(e) => handleCustomQuestionChange(e.target.value)}
                        onFocus={() => handleQuestionChoice('custom')}
                        placeholder="Введите ваш вопрос (максимум 100 символов)"
                        maxLength={100}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {customQuestion.length}/100 символов
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Опция: анализ без вопроса */}
                <div className="p-3">
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="radio"
                      name="questionChoice"
                      value="none"
                      checked={questionChoice === 'none'}
                      onChange={() => handleQuestionChoice('none')}
                      className="text-primary-600 focus:ring-primary-500 mt-1"
                    />
                    <div className="ml-3">
                      <span className="font-medium text-gray-900">
                        Провести анализ без конкретного вопроса
                      </span>
                      <p className="text-sm text-gray-600 mt-1">
                        ИИ проанализирует ситуацию в целом и выделит общие плюсы и минусы
                      </p>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Кнопки действий */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={handleQuestionModalCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Отменить анализ
                </button>
                <button
                  onClick={handleQuestionModalSubmit}
                  disabled={
                    !questionChoice || 
                    (questionChoice === 'custom' && !customQuestion.trim())
                  }
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Продолжить анализ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonalLanding; 